/**
 * Etichette, toni e formattazioni condivise della coda ticket di assistenza
 * (pagina /ticket + drawer dettaglio). Solo presentazione: i valori canonici
 * vivono in `types/supportTickets.ts`.
 *
 * Le formattazioni data riusano quelle del CRM di piattaforma
 * (`platform-crm/labels.ts`) — stessa console, stessa resa, zero duplicati.
 */

import { Timestamp } from 'firebase/firestore';
import type { SoftChipTone } from '../SoftChip';
import type {
  SupportTicketCategoria,
  SupportTicketPriorita,
  SupportTicketStato,
} from '../../types/supportTickets';

export { formatDate, formatDateTime } from '../platform-crm/labels';

// ============================================================================
// STATO
// ============================================================================

/** Ordine canonico del ciclo di vita (usato per select e filtri). */
export const TICKET_STATO_ORDER: SupportTicketStato[] = [
  'aperto',
  'in_lavorazione',
  'in_attesa_cliente',
  'risolto',
  'chiuso',
];

export const TICKET_STATO_LABELS: Record<SupportTicketStato, string> = {
  aperto: 'Aperto',
  in_lavorazione: 'In lavorazione',
  in_attesa_cliente: 'In attesa cliente',
  risolto: 'Risolto',
  chiuso: 'Chiuso',
};

export const TICKET_STATO_TONES: Record<SupportTicketStato, SoftChipTone> = {
  aperto: 'warning',
  in_lavorazione: 'info',
  in_attesa_cliente: 'primary',
  risolto: 'success',
  chiuso: 'default',
};

// ============================================================================
// CATEGORIA
// ============================================================================

export const TICKET_CATEGORIA_ORDER: SupportTicketCategoria[] = [
  'anomalia',
  'domanda',
  'richiesta_funzionalita',
  'collegamento',
];

export const TICKET_CATEGORIA_LABELS: Record<SupportTicketCategoria, string> = {
  anomalia: 'Anomalia',
  domanda: 'Domanda',
  richiesta_funzionalita: 'Richiesta funzionalità',
  collegamento: 'Richiesta di collegamento',
};

// ============================================================================
// PRIORITÀ
// ============================================================================

export const TICKET_PRIORITA_ORDER: SupportTicketPriorita[] = [
  'bassa',
  'normale',
  'alta',
];

export const TICKET_PRIORITA_LABELS: Record<SupportTicketPriorita, string> = {
  bassa: 'Bassa',
  normale: 'Normale',
  alta: 'Alta',
};

export const TICKET_PRIORITA_TONES: Record<SupportTicketPriorita, SoftChipTone> = {
  bassa: 'default',
  normale: 'info',
  alta: 'error',
};

// ============================================================================
// TEMPI
// ============================================================================

/** Millisecondi da un campo timestamp Firestore (null se assente/pending). */
export function tsMillis(value: unknown): number | null {
  return value instanceof Timestamp ? value.toMillis() : null;
}

/**
 * Durata leggibile da millisecondi: "42 min", "3 h 20 min", "2 g 4 h".
 * Valori non validi o negativi → '—' (mai numeri assurdi in UI).
 */
export function formatDurataMs(ms: number | null | undefined): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return '—';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ${minutes % 60} min`;
  const days = Math.floor(hours / 24);
  return `${days} g ${hours % 24} h`;
}

/**
 * Durata tra due timestamp Firestore ('—' se uno dei due manca).
 * Usata per le metriche prima-risposta e risoluzione del singolo ticket.
 */
export function formatDurataTra(from: unknown, to: unknown): string {
  const fromMs = tsMillis(from);
  const toMs = tsMillis(to);
  if (fromMs === null || toMs === null) return '—';
  return formatDurataMs(toMs - fromMs);
}
